import { useEffect, useRef } from "react";

/**
 * Attaches Google Maps Places Autocomplete to an input ref.
 * Polls until both the input element and the Autocomplete constructor are ready,
 * so it works for inputs that mount conditionally (e.g. inside a multi-step modal).
 *
 * @param {React.RefObject<HTMLInputElement>} inputRef
 * @param {(formattedAddress: string) => void} onSelect
 * @param {{ types?: string[], deps?: any[], country?: string }} opts
 */
export default function useAddressAutocomplete(inputRef, onSelect, opts = {}) {
  const { types = ["geocode"], deps = [], country = "gb" } = opts;
  const onSelectRef = useRef(onSelect);
  useEffect(() => { onSelectRef.current = onSelect; });

  useEffect(() => {
    // Ensure Google Maps script is requested.
    if (typeof window.loadGoogleMaps === "function") window.loadGoogleMaps();

    // Force the Autocomplete dropdown above any modal/overlay, injected once.
    if (!window.__tsPacZ) {
      window.__tsPacZ = true;
      const s = document.createElement("style");
      s.textContent = ".pac-container{z-index:2147483647 !important;}";
      document.head.appendChild(s);
    }

    // Fire-and-forget: import the Places library via importLibrary if available
    // (needed for loading=async mode; harmless in traditional mode).
    const ensurePlaces = () => {
      if (window.__tsPlacesImported) return;
      const importLib = window.google?.maps?.importLibrary;
      if (typeof importLib === "function") {
        window.__tsPlacesImported = true;
        importLib("places").catch(() => {});
      }
    };

    let iv = null, stop = null;

    const clear = () => {
      if (iv) { clearInterval(iv); iv = null; }
      if (stop) { clearTimeout(stop); stop = null; }
    };

    const attach = (input) => {
      const AC = window.google?.maps?.places?.Autocomplete;
      if (typeof AC !== "function") return false;
      try {
        const ac = new AC(input, {
          types, componentRestrictions: { country }, fields: ["formatted_address"],
        });
        ac.addListener("place_changed", () => {
          const place = ac.getPlace();
          if (place?.formatted_address) onSelectRef.current(place.formatted_address);
        });
        input.addEventListener("keydown", (e) => { if (e.key === "Enter") e.preventDefault(); });
        input._tsAcAttached = true;
        return true;
      } catch { return false; }
    };

    iv = setInterval(() => {
      const input = inputRef?.current;
      if (!input) return;
      if (input._tsAcAttached) { clear(); return; }
      ensurePlaces();
      if (attach(input)) clear();
    }, 200);
    stop = setTimeout(clear, 20000);

    return clear;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef, ...deps]);
}